import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  effect,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { ModalService } from '../../utils/services/modal.service';
import { BusinessService } from '../../services/business.service';
import { PaymentAccount, AccountTypeCode } from '../../schemas/payment_account';
import { PaymentProvider } from '../../schemas/payment_provider';
import { Locale } from '../../schemas/locale';
import { TextSearchFilterPipe } from '../../utils/pipes/text-search-filter.pipe';
import { ModalComponent } from '../modal/modal.component';
import { ConfettiService } from '../../utils/services/confetti.service';

@Component({
  selector: 'qwiko-manage-payment-accounts',
  imports: [
    CommonModule,
    TranslocoModule,
    FormsModule,
    TextSearchFilterPipe,
    ModalComponent,
  ],
  providers: [ConfettiService],
  templateUrl: './manage-payment-accounts.component.html',
  styleUrl: './manage-payment-accounts.component.scss',
})
export class ManagePaymentAccountsComponent implements OnInit {
  modalService = inject(ModalService);
  businessService = inject(BusinessService);
  confettiService = inject(ConfettiService);

  loading = model<boolean>(false);
  isSelectProvider = model<boolean>();
  isListAccounts = model<boolean>();
  selectedAccount = model<PaymentAccount | null>();
  context = model<string>('');
  isEditAccount = signal<boolean>(false);
  isAddAccount = signal<boolean>(false);
  isDeleteAccount = signal<boolean>(false);

  isDirectEdit = signal<boolean>(false);
  isDirectEditEffect = effect(() => {
    const hasSelectedAccount = this.selectedAccount() ? true : false;
    if (this.isListAccounts() && hasSelectedAccount) {
      this.isDirectEdit.set(true);
      this.editAccount();
    }
  });

  business = this.businessService.business;
  accountNameEffect = effect(() => {
    if (this.business()) {
      this.accountName.set(this.businessService.business()?.name!);
    }
  });
  businessPaymentAccounts = this.businessService.businessPaymentAccounts;

  country = computed<Locale>(() => {
    return this.businessService.business()?.country as Locale;
  });

  accountTypeCode = AccountTypeCode;

  paymentProviderFilter = signal<string>('');
  paymentProviders = signal<PaymentProvider[]>([]);
  accountNumber = signal<string>('');
  accountNumberFormatted = computed<string>(() => {
    const code = this.country()?.phone_code;
    let accountType = '';
    if (this.isEditAccount()) {
      accountType = this.selectedAccount()?.type?.code!;
    } else {
      accountType = this.selectedProvider()?.account_type?.code!;
    }
    // const code = this.country()?.phone_code?.replace("+", '');
    if (accountType === AccountTypeCode.MOMO) {
      return `${code}${this.accountNumber()}`;
    }

    return this.accountNumber();
  });

  accountNumberDisplay = computed<string>(() => {
    const code = this.country()?.phone_code;
    const starts_with = this.country()?.starts_with;

    let accountType = '';
    if (this.isEditAccount()) {
      accountType = this.selectedAccount()?.type?.code!;
    } else {
      accountType = this.selectedProvider()?.account_type?.code!;
    }
    // const code = this.country()?.phone_code?.replace("+", '');
    if (accountType === AccountTypeCode.MOMO) {
      if (this.accountNumberFormatted().startsWith(code!) && starts_with) {
        const index = this.accountNumberFormatted().indexOf(starts_with!);

        if (index !== -1) {
          const sliceIndex = index + starts_with!.length;
          const remainder = this.accountNumberFormatted().slice(sliceIndex);

          return `${starts_with}${remainder}`;
        }
      }
    }

    return this.accountNumberFormatted();
  });
  accountName = signal<string>('');
  accountLabel = signal<string>('');

  selectedProvider = signal<PaymentProvider | null>(null);

  hasInit = signal<boolean>(false);
  initEffect = effect(async () => {
    if (
      (!this.hasInit() && this.isListAccounts()) ||
      (!this.hasInit() && this.isSelectProvider())
    ) {
      this.init();
      this.hasInit.set(true);
    }
  });

  constructor() {
    // effect(async () => {
    //   if (this.businessService.businessDataLoaded()) {
    //     await this.getPaymentProviders();
    //     this.accountName.set(this.businessService.business()?.name!);
    //   }
    // });
  }

  async ngOnInit(): Promise<void> {
    // Fall back to effect if business data is not ready
    // if (this.businessService.businessDataLoaded()) {
    //   await this.init();
    //   this.accountName.set(this.businessService.business()?.name!);
    //   return;
    // }
  }

  async init() {
    this.loading.set(true);
    this.paymentProviders.set([]);
    this.businessPaymentAccounts.set([]);
    // await new Promise((resolve) => setTimeout(resolve, 3000));
    await this.businessService.getBusinessPaymentAccounts(false);
    const paymentProviders =
      await this.businessService.getPaymentProviders(false);
    this.paymentProviders.set(paymentProviders);
    this.loading.set(false);
    // this.isSelectProvider.set(true);
  }

  async addPaymentAccount() {
    this.loading.set(true);
    this.isSelectProvider.set(true);
    this.isListAccounts.set(false);
    await this.init();
    this.loading.set(false);
  }

  editAccount(account?: PaymentAccount) {
    this.isListAccounts.set(false);
    this.isEditAccount.set(true);
    this.isDeleteAccount.set(false);

    if (account) {
      this.selectedAccount.set(account);
    }

    this.accountName.set(this.selectedAccount()?.name!);
    this.accountNumber.set(this.selectedAccount()?.number!);
    this.formatPhoneNumber();
    this.accountLabel.set(this.selectedAccount()?.label!);
  }

  selectProvider(provider: any) {
    this.selectedProvider.set(provider);
    // this.openModal('manage-payment-accounts');
    this.isSelectProvider.set(false);
    this.isAddAccount.set(true);
  }

  async saveAccount() {
    this.loading.set(true);
    const account: PaymentAccount = {
      name: this.accountName(),
      number: this.accountNumberFormatted(),
      number_display: this.accountNumberDisplay(),
      label: this.accountLabel(),
      type: {
        id: this.selectedProvider()!.account_type!.id,
      },
      payment_provider: {
        id: this.selectedProvider()!.id,
      },
      country: this.country()?.id,
    };
    // await new Promise((resolve) => setTimeout(resolve, 3000));
    // console.log(account);
    await this.businessService.createBusinessPaymentAccount(account);
    if (this.context() === 'ONBOARDING') {
      this.closeModal('close');
    } else {
      this.closeModal('manage-payment-accounts');
    }

    this.loading.set(false);
  }

  async updateAccount() {
    this.loading.set(true);
    // await new Promise((resolve) => setTimeout(resolve, 3000));
    // console.log(account);
    const account: PaymentAccount = {
      id: this.selectedAccount()?.id,
      name: this.accountName(),
      number: this.accountNumberFormatted(),
      number_display: this.accountNumberDisplay(),
      label: this.accountLabel(),
    };
    await this.businessService.updateBusinessPaymentAccount(account);
    this.closeModal('manage-payment-accounts');
    this.loading.set(false);
  }

  async softDeleteAccount() {
    if (!this.isDeleteAccount()) {
      this.isEditAccount.set(false);
      this.isDeleteAccount.set(true);
      return;
    }

    this.loading.set(true);
    // const account: PaymentAccount = {
    //   name: this.accountName(),
    //   number: this.formatPhoneNumber(),
    //   label: this.accountLabel(),
    // };
    // await new Promise((resolve) => setTimeout(resolve, 3000));
    // console.log(account);
    await this.businessService.softDeleteBusinessPaymentAccount(
      this.selectedAccount()!,
    );
    this.back('manage-payment-accounts');
    this.loading.set(false);
  }

  formatPhoneNumber() {
    const code = this.country()?.phone_code;
    const starts_with = this.country()?.starts_with;
    let number: string | number = this.accountNumber();

    let accountType = '';
    if (this.isEditAccount()) {
      accountType = this.selectedAccount()?.type?.code!;
    } else {
      accountType = this.selectedProvider()?.account_type?.code!;
    }

    if (this.accountNumber() == '') {
      return;
    }

    number = this.accountNumber().split(' ').join('');

    if (this.isEditAccount() && number.startsWith(code!)) {
      number = number.replace(code!, '');
    }

    if (accountType === this.accountTypeCode.MOMO) {
      number = parseInt(number);

      if (isNaN(number) || number == 0) {
        this.accountNumber.set('');
        return;
      }

      number = number.toString();
    } else {
      number = number.replace(/[a-zA-Z]/g, '');
      this.accountNumber.set(number);
    }

    this.accountNumber.set(number);

    return `${this.accountNumber()}`;
  }

  formatOnPaste(event: ClipboardEvent) {
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    if (this.selectedProvider()?.account_type?.code === AccountTypeCode.MOMO) {
      const phoneCode = this.country()?.phone_code ?? '';

      if (pastedText?.startsWith(phoneCode)) {
        pastedText = pastedText.substring(phoneCode.length);
      } else if (pastedText?.startsWith(phoneCode.substring(1))) {
        pastedText = pastedText.substring(phoneCode.length - 1);
      }
    }

    this.accountNumber.set(pastedText ?? '');
    this.formatPhoneNumber();
  }

  openModal(modal: string) {
    this.modalService.open(modal);
  }

  closeModal(modal: string) {
    if (modal === 'close') {
      this.modalService.closeAll();
      this.resetAllStates();
      return;
    }

    if (this.isDirectEdit()) {
      this.resetAllStates();
      this.modalService.close(modal);
    } else {
      this.back();
    }
  }

  resetAllStates() {
    this.isSelectProvider.set(false);
    this.isAddAccount.set(false);
    this.isEditAccount.set(false);
    this.isDeleteAccount.set(false);
    this.isListAccounts.set(false);
    this.isDirectEdit.set(false);

    this.selectedProvider.set(null);
    this.selectedAccount.set(null);
    this.accountNumber.set('');
    this.accountName.set(this.businessService.business()?.name!);
    this.accountLabel.set('');
  }

  back(modal?: string) {
    if (this.isEditAccount() || this.context() == 'SETTINGS') {
      this.resetAllStates();
      this.isListAccounts.set(true);
      this.isEditAccount.set(false);
    } else {
      if (modal) {
        this.modalService.closeAll();
        this.resetAllStates();
        this.openModal(modal);
        return;
      }

      this.resetAllStates();
      this.isSelectProvider.set(true);
      this.isAddAccount.set(false);
    }
  }
}
